import { forwardRef, useState, useEffect, useRef, useCallback } from "react";
import { Input } from "./input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./select";

export interface SizeInputProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    units?: string[];
    defaultUnit?: string;
    step?: number;
    min?: number;
    max?: number;
}

const DEFAULT_UNITS = ["px", "%", "rem", "em", "vh", "vw", "auto"];
const SPECIAL_UNITS = ["auto", "none", "inherit", "initial", "unset", "fit-content", "max-content", "min-content"];

export const SizeInput = forwardRef<HTMLInputElement, SizeInputProps>(
    (
        {
            value = "",
            onChange,
            placeholder = "auto",
            className = "",
            units = DEFAULT_UNITS,
            defaultUnit = "px",
            step = 1,
            min = -Infinity,
            max = Infinity,
            ...props
        },
        ref
    ) => {
        const [numericValue, setNumericValue] = useState("");
        const [selectedUnit, setSelectedUnit] = useState(defaultUnit);
        const inputRef = useRef<HTMLInputElement | null>(null);

        // Parse the input value to separate numeric and unit parts
        useEffect(() => {
            if (value) {
                // Handle special cases like "auto", "inherit", "none", etc.
                if (units.includes(value)) {
                    setNumericValue("");
                    setSelectedUnit(value);
                    return;
                }

                // Extract numeric value and unit - improved regex to handle decimals and negative values
                const match = value.match(/^(-?\d*\.?\d+)(.*)$/);
                if (match) {
                    const [, numeric, unit] = match;
                    setNumericValue(numeric);

                    // Use the unit from the value if it's valid, otherwise use default
                    const foundUnit = units.find(u => unit === u) || units.find(u => unit.trim() === u);
                    setSelectedUnit(foundUnit || defaultUnit);
                } else {
                    // If no numeric value found, check if it's a valid unit
                    const foundUnit = units.find(u => value.trim() === u);
                    if (foundUnit) {
                        setNumericValue("");
                        setSelectedUnit(foundUnit);
                    } else {
                        setNumericValue("");
                        setSelectedUnit(defaultUnit);
                    }
                }
            } else {
                setNumericValue("");
                setSelectedUnit(defaultUnit);
            }
        }, [value, units, defaultUnit]);

        // Handle ref forwarding
        useEffect(() => {
            if (ref) {
                if (typeof ref === "function") {
                    ref(inputRef.current!);
                } else {
                    (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputRef.current;
                }
            }
        }, [ref]);

        const handleIncrement = useCallback(() => {
            if (SPECIAL_UNITS.includes(selectedUnit)) return;

            const currentValue = parseFloat(numericValue) || 0;
            const newValue = Math.min(currentValue + step, max);
            const newValueStr = newValue.toString();

            setNumericValue(newValueStr);
            const fullValue = `${newValueStr}${selectedUnit}`;
            onChange?.(fullValue);
        }, [numericValue, selectedUnit, step, max, onChange]);

        const handleDecrement = useCallback(() => {
            if (SPECIAL_UNITS.includes(selectedUnit)) return;

            const currentValue = parseFloat(numericValue) || 0;
            const newValue = Math.max(currentValue - step, min);
            const newValueStr = newValue.toString();

            setNumericValue(newValueStr);
            const fullValue = `${newValueStr}${selectedUnit}`;
            onChange?.(fullValue);
        }, [numericValue, selectedUnit, step, min, onChange]);

        // Add keyboard event listener for arrow keys
        useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                const el = inputRef.current;
                if (!el || document.activeElement !== el) return;

                if (e.key === "ArrowUp") {
                    e.preventDefault();
                    handleIncrement();
                } else if (e.key === "ArrowDown") {
                    e.preventDefault();
                    handleDecrement();
                }
            };

            window.addEventListener("keydown", handleKeyDown);
            return () => {
                window.removeEventListener("keydown", handleKeyDown);
            };
        }, [handleIncrement, handleDecrement]);

        const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setNumericValue(newValue);

            // Construct the full value
            let fullValue = "";
            if (SPECIAL_UNITS.includes(selectedUnit)) {
                // For special units, use the unit directly regardless of numeric value
                fullValue = selectedUnit;
            } else {
                fullValue = newValue ? `${newValue}${selectedUnit}` : "";
            }

            onChange?.(fullValue);
        };

        const handleUnitChange = (newUnit: string) => {
            setSelectedUnit(newUnit);

            // Construct the full value
            let fullValue = "";
            if (SPECIAL_UNITS.includes(newUnit)) {
                fullValue = newUnit;
            } else if (numericValue) {
                fullValue = `${numericValue}${newUnit}`;
            } else {
                fullValue = "";
            }

            onChange?.(fullValue);
        };

        return (
            <div className={`flex ${className}`}>
                <Input
                    ref={inputRef}
                    type="text"
                    value={SPECIAL_UNITS.includes(selectedUnit) ? "" : numericValue}
                    onChange={handleNumericChange}
                    placeholder={SPECIAL_UNITS.includes(selectedUnit) ? selectedUnit : "0"}
                    disabled={SPECIAL_UNITS.includes(selectedUnit)}
                    className="rounded-r-none border-r-0 h-8 bg-background border-border text-foreground text-xs focus:border-primary focus:ring-0"
                    {...props}
                />
                <Select value={selectedUnit} onValueChange={handleUnitChange}>
                    <SelectTrigger className="w-32 rounded-l-none h-8 bg-background border-border text-foreground text-xs focus:border-primary focus:ring-0">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {units.map((unit) => (
                            <SelectItem key={unit} value={unit} className="text-xs">
                                {unit}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }
);

SizeInput.displayName = "SizeInput";